import { AUTH_ROLES, type AuthProvider, type AuthRole, type AuthState, type AuthenticatedUser } from './model.js';
import { primarySecurityRole } from '../../authorization/model.js';
import type { EntraBrowserAuthConfig } from './config.js';
import {
  EntraClientError,
  type EntraAccountSnapshot,
  type EntraAuthenticationSnapshot,
  type EntraClientAdapter,
} from './entra-client.js';

export class EntraAuthProvider implements AuthProvider {
  private state: AuthState = { status: 'INITIALIZING' };
  private readonly listeners = new Set<(state: AuthState) => void>();
  private activeAccountId: string | null = null;
  private identityToken: string | null = null;

  constructor(
    private readonly client: EntraClientAdapter,
    private readonly config: EntraBrowserAuthConfig,
  ) {}

  getState(): AuthState {
    return this.state;
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async initialize(): Promise<void> {
    this.setState({ status: 'INITIALIZING' });
    try {
      await this.client.initialize();
      const redirectResult = await this.client.handleRedirect();
      if (redirectResult !== null) {
        this.acceptAuthentication(redirectResult);
        return;
      }

      const account = this.client.getAccounts()[0];
      if (account === undefined) {
        this.setState({ status: 'UNAUTHENTICATED' });
        return;
      }
      await this.establishFromCachedAccount(account);
    } catch (error) {
      this.handleAuthenticationError(error, false);
    }
  }

  async refreshSession(): Promise<void> {
    if (this.state.status !== 'AUTHENTICATED' || this.activeAccountId === null) return;
    try {
      this.acceptAuthentication(await this.client.acquireTokenSilent(this.activeAccountId));
    } catch (error) {
      this.handleAuthenticationError(error, true);
    }
  }

  async getIdentityToken(): Promise<string | null> {
    if (this.state.status !== 'AUTHENTICATED') return null;
    await this.refreshSession();
    return this.state.status === 'AUTHENTICATED' ? this.identityToken : null;
  }

  async signIn(): Promise<void> {
    try {
      await this.client.loginRedirect();
    } catch (error) {
      this.handleAuthenticationError(error, false);
    }
  }

  async signOut(): Promise<void> {
    if (this.activeAccountId === null) {
      this.setState({ status: 'UNAUTHENTICATED' });
      return;
    }
    const accountId = this.activeAccountId;
    this.activeAccountId = null;
    this.identityToken = null;
    try {
      await this.client.logoutRedirect(accountId);
      this.setState({ status: 'UNAUTHENTICATED' });
    } catch (error) {
      this.handleAuthenticationError(error, false);
    }
  }

  private async establishFromCachedAccount(account: EntraAccountSnapshot): Promise<void> {
    this.activeAccountId = account.homeAccountId;
    this.client.setActiveAccount(account.homeAccountId);
    this.acceptAuthentication(await this.client.acquireTokenSilent(account.homeAccountId));
  }

  private acceptAuthentication(result: EntraAuthenticationSnapshot): void {
    this.activeAccountId = result.account.homeAccountId;
    this.client.setActiveAccount(result.account.homeAccountId);
    this.identityToken = result.identityToken;
    const identity = deriveAuthenticatedUser(result, this.config.tenantId);
    if (identity === null) {
      this.setState({
        status: 'ACCESS_DENIED',
        message: 'Access is not assigned. A recognized MSP Health app role is required.',
      });
      return;
    }
    this.setState({ status: 'AUTHENTICATED', user: identity });
  }

  private handleAuthenticationError(error: unknown, duringRenewal: boolean): void {
    if (error instanceof EntraClientError && error.kind === 'CANCELLED') {
      this.setState({ status: 'UNAUTHENTICATED', message: 'Microsoft sign-in was canceled.' });
      return;
    }
    if (error instanceof EntraClientError && error.kind === 'INTERACTION_REQUIRED') {
      this.activeAccountId = null;
      this.identityToken = null;
      this.setState({
        status: 'UNAUTHENTICATED',
        message: duringRenewal
          ? 'Your Microsoft session expired. Sign in again to continue.'
          : 'Microsoft sign-in is required to continue.',
      });
      return;
    }
    this.setState({
      status: 'AUTHENTICATION_ERROR',
      message: 'Microsoft authentication could not be completed. Try again.',
    });
  }

  private setState(state: AuthState): void {
    this.state = state;
    for (const listener of this.listeners) listener(state);
  }
}

export function deriveAuthenticatedUser(
  result: EntraAuthenticationSnapshot,
  configuredTenantId: string,
): AuthenticatedUser | null {
  const claims = { ...result.account.idTokenClaims, ...result.idTokenClaims };
  const tenantId = claimString(claims, 'tid') ?? result.account.tenantId;
  if (tenantId.toLowerCase() !== configuredTenantId.toLowerCase()) return null;

  const roles = claimRoles(claims);
  const role = primarySecurityRole(roles);
  if (role === null) return null;

  const displayName = claimString(claims, 'name') ?? result.account.name ?? 'Microsoft user';
  const email = claimString(claims, 'preferred_username') ?? result.account.username;
  const subjectId = claimString(claims, 'oid');
  if (subjectId === undefined) return null;
  return {
    subjectId,
    tenantId,
    tenantName: 'Positive Output',
    displayName,
    email,
    roles,
    role,
    operationalProfile: 'GENERAL',
  };
}

function claimRoles(claims: Readonly<Record<string, unknown>>): readonly AuthRole[] {
  const value = claims.roles;
  const candidates = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  return AUTH_ROLES.filter((role) => candidates.includes(role));
}

function claimString(claims: Readonly<Record<string, unknown>>, name: string): string | undefined {
  const value = claims[name];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}
